/**
 * SaveManager - Handles game progress persistence
 * Uses localStorage for web builds
 */

const SAVE_KEY = 'bigblueball_save';

export default class SaveManager {
    constructor() {
        this.data = {
            unlockedLevels: [1],
            levelScores: {},
            bestTimes: {},
            settings: {
                sfxVolume: 0.7,
                musicVolume: 0.4,
                isMuted: false
            },
            totalPlayTime: 0,
            lastPlayed: null
        };
        
        this.load();
    }

    /**
     * Save current data to localStorage
     */
    save() {
        try {
            this.data.lastPlayed = Date.now();
            localStorage.setItem(SAVE_KEY, JSON.stringify(this.data));
            return true;
        } catch (e) {
            console.error('SaveManager: Failed to save', e);
            return false;
        }
    }

    /**
     * Load data from localStorage
     */
    load() {
        try {
            const saved = localStorage.getItem(SAVE_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                this.data = { ...this.data, ...parsed };
            }
        } catch (e) {
            console.error('SaveManager: Failed to load', e);
        }
    }

    /**
     * Unlock a level
     */
    unlockLevel(level) {
        if (!this.data.unlockedLevels.includes(level)) {
            this.data.unlockedLevels.push(level);
            this.data.unlockedLevels.sort((a, b) => a - b);
            this.save();
        }
    }

    /**
     * Check if a level is unlocked
     */
    isLevelUnlocked(level) {
        return this.data.unlockedLevels.includes(level);
    }

    /**
     * Get all unlocked levels
     */
    getUnlockedLevels() {
        return [...this.data.unlockedLevels];
    }

    /**
     * Save level score
     */
    saveLevelScore(level, score) {
        const currentBest = this.data.levelScores[level] || 0;
        if (score > currentBest) {
            this.data.levelScores[level] = score;
            this.save();
            return true; // New high score
        }
        return false;
    }

    /**
     * Save level time
     */
    saveLevelTime(level, time) {
        const currentBest = this.data.bestTimes[level] || Infinity;
        if (time < currentBest) {
            this.data.bestTimes[level] = time;
            this.save();
            return true; // New best time
        }
        return false;
    }

    /**
     * Get level score
     */
    getLevelScore(level) {
        return this.data.levelScores[level] || 0;
    }

    /**
     * Get level best time
     */
    getLevelTime(level) {
        return this.data.bestTimes[level] || null;
    }

    /**
     * Get settings
     */
    getSettings() {
        return { ...this.data.settings };
    }

    /**
     * Update settings
     */
    updateSettings(settings) {
        this.data.settings = { ...this.data.settings, ...settings };
        this.save();
    }

    /**
     * Add to total play time
     */
    addPlayTime(ms) {
        this.data.totalPlayTime += ms;
        this.save();
    }

    /**
     * Get total play time
     */
    getTotalPlayTime() {
        return this.data.totalPlayTime;
    }

    /**
     * Clear all save data
     */
    clearAll() {
        localStorage.removeItem(SAVE_KEY);
        this.data = {
            unlockedLevels: [1],
            levelScores: {},
            bestTimes: {},
            settings: {
                sfxVolume: 0.7,
                musicVolume: 0.4,
                isMuted: false
            },
            totalPlayTime: 0,
            lastPlayed: null
        };
    }

    /**
     * Export save data as JSON string
     */
    export() {
        return JSON.stringify(this.data, null, 2);
    }

    /**
     * Import save data from JSON string
     */
    import(jsonString) {
        try {
            const data = JSON.parse(jsonString);
            this.data = { ...this.data, ...data };
            this.save();
            return true;
        } catch (e) {
            console.error('SaveManager: Invalid import data', e);
            return false;
        }
    }
}
